import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Clipboard } from '@angular/cdk/clipboard';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, of } from 'rxjs';
import { CompanyMetricIngestHelpResponse } from 'src/app/models/company-metric.model';
import { ApiService } from 'src/app/services/api.service';

export interface CompanyObjectiveKeyResultIngestHelpDialogData {
  key_result_title: string;
  company_metric_slug: string;
  company_metric_title: string;
  company_metric_relatable: 'company' | 'product';
  company_product_id: number;
  unit: string | null;
}

type IngestEndpointMode = 'current' | 'legacy';

@Component({
  selector: 'app-company-objective-key-result-ingest-help-dialog',
  templateUrl: './company-objective-key-result-ingest-help-dialog.component.html',
  styleUrl: './company-objective-key-result-ingest-help-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CdkScrollable,
    MatButtonModule,
    MatButtonToggleModule,
    MatDialogActions,
    MatDialogContent,
    MatDialogTitle,
    MatIconModule,
    MatTooltipModule,
  ],
})
export class CompanyObjectiveKeyResultIngestHelpDialogComponent {
  private readonly dialog_ref = inject(MatDialogRef<CompanyObjectiveKeyResultIngestHelpDialogComponent>);
  private readonly clipboard = inject(Clipboard);
  private readonly api = inject(ApiService);
  readonly data = inject<CompanyObjectiveKeyResultIngestHelpDialogData>(MAT_DIALOG_DATA);

  readonly apexcharts_docs_url = 'https://apexcharts.com/docs/options/';
  readonly is_product_metric = this.data.company_metric_relatable === 'product';
  readonly endpoint_mode = signal<IngestEndpointMode>('current');

  readonly help = toSignal(
    this.api
      .getCompanyMetricIngestHelp(this.data.company_product_id, {
        company_metric_slug: this.data.company_metric_slug,
        company_metric_title: this.data.company_metric_title,
        company_metric_relatable: this.data.company_metric_relatable,
        unit: this.data.unit,
      })
      .pipe(catchError(() => of(null as CompanyMetricIngestHelpResponse | null))),
    { initialValue: null as CompanyMetricIngestHelpResponse | null }
  );

  readonly endpoint = computed(() => {
    const help = this.help();
    if (help === null) {
      return null;
    }

    return this.endpoint_mode() === 'current' ? help.current.endpoint : help.legacy.endpoint;
  });

  readonly show_legacy_company_warning = computed(
    () => this.endpoint_mode() === 'legacy' && this.help()?.legacy.unavailable_reason === 'company_level_metric'
  );

  readonly show_legacy_missing_uuid = computed(
    () => this.endpoint_mode() === 'legacy' && this.is_product_metric && this.help()?.legacy.endpoint === null
  );

  readonly show_ingest_details = computed(() => {
    if (this.help() === null) {
      return false;
    }

    return this.endpoint_mode() === 'current' || (this.is_product_metric && this.endpoint() !== null);
  });

  readonly show_current_contract_notes = computed(() => this.endpoint_mode() === 'current');

  readonly show_legacy_contract_notes = computed(() => this.endpoint_mode() === 'legacy' && this.show_ingest_details());

  readonly current_label = computed(() => this.help()?.current.label ?? 'Versión actual');

  readonly legacy_label = computed(() => this.help()?.legacy.label ?? 'Legacy (hasta Agosto 2026)');

  readonly example_payload = computed(() => {
    const help = this.help();
    if (help === null) {
      return '';
    }

    const payload = this.endpoint_mode() === 'current' ? help.current.payload : help.legacy.payload;
    if (payload === null) {
      return '';
    }

    return JSON.stringify(payload, null, 2);
  });

  readonly company_product_uuid = computed(() => this.help()?.company_product_uuid ?? null);

  readonly company_uuid = computed((): string | null => this.help()?.company_uuid ?? null);

  setEndpointMode(mode: IngestEndpointMode): void {
    this.endpoint_mode.set(mode);
  }

  copy(value: string): void {
    this.clipboard.copy(value);
  }

  close(): void {
    this.dialog_ref.close();
  }
}
